import { useEffect, useRef, type RefObject } from "react";

/**
 * Which cell the caret is in.
 *
 * One job: a language change rewrites a sheet's numbers into the new language,
 * and it must not do that to the cell someone is typing in. A number can be
 * composed across a change, and rewriting it would edit the user's own
 * characters — and move the caret — on an event they did not cause.
 *
 * That is all this needs to be. An earlier version also latched the LANGUAGE
 * the composition began in, which turned out to be redundant: the cell's own
 * stamp already records the language of the text that is there, `stampForEdit`
 * already prefers it over the app's current setting, and this focus gate is
 * what keeps that stamp from going stale under a caret. The two sequences the
 * latch existed for both hold without it:
 *
 *   - "1,2" typed in English, then the language changes. The rewrite declines
 *     to translate half-typed text, so the cell keeps its English stamp, and
 *     the next keystroke takes that stamp rather than the new language.
 *   - "1" typed in English, then the language changes. "1" IS translatable, but
 *     the caret is in the cell, so this gate stops the rewrite; the stamp stays
 *     English and so does the rest of the number.
 *
 * Cells are identified by their `data-cell` attribute (`<rowId>:<column>`),
 * which both sheets already carry for keyboard navigation.
 */
export type CaretCell = {
  /** True while the caret is in this cell, so a rewrite must leave it alone. */
  isComposing: (cell: string) => boolean;
};

export function useCaretCell(containerRef: RefObject<HTMLElement | null>): CaretCell {
  const caret = useRef<string | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const cellOf = (target: EventTarget | null): string | null => {
      if (!(target instanceof HTMLElement)) return null;
      return target.closest<HTMLElement>("[data-cell]")?.dataset.cell ?? null;
    };
    const onFocusIn = (event: FocusEvent) => {
      caret.current = cellOf(event.target);
    };
    const onFocusOut = (event: FocusEvent) => {
      // Only release when the caret leaves for something that is not a cell;
      // moving between cells re-takes it through focusin.
      if (!cellOf(event.relatedTarget)) caret.current = null;
    };

    node.addEventListener("focusin", onFocusIn);
    node.addEventListener("focusout", onFocusOut);
    return () => {
      node.removeEventListener("focusin", onFocusIn);
      node.removeEventListener("focusout", onFocusOut);
    };
  }, [containerRef]);

  return { isComposing: (cell) => caret.current === cell };
}
